import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

import {
  LensState,
  MaterializationType,
  ModelLayer,
  NodeStatus,
  TestStatus,
  useDagContext,
} from '@dbt-labs/dbt-dag';
import { zParseSelectFlags } from '@dbt-labs/selector';

import {
  AutoExposureBiProvider,
  EnvironmentDeploymentType,
  ModelQueryHistoryState,
  ModelQueryHistoryStatus,
  ResourceNodeType,
} from '@/__generated__/graphql';
import { QUERY_USAGE_TOOLTIP_NO_DATA } from '@/components/Charts/ModelQueryUsageBarChart';
import { ExplorerDagNode } from '@/components/ExplorerDag';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useDiscoveryAppliedLineage } from '@/hooks/discovery/appliedLineage';
import { AccessType } from '@/typings/dbt';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { truthy, unique } from '@/util/array';
import { getHumanReadableQueryError } from '@/util/formatErrors';
import { toSearchResourceTypes } from '@/util/resourceType';
import { OneOf } from '@/util/type';
import { useModelQueryHistoryState } from '../discovery/modelQueryHistoryState';
import { useAllPublicModels } from '../discovery/publicModels';
import { useProducerProjects } from '../discovery/relatedProjects';
import { ExternalConnections, useDependentProjects } from '../useDependentProjects';
import { useEnvironmentScope } from '../useEnvironmentScope';

type ToastMessage = Parameters<typeof toast.error>[0];

export type AppliedLineageResult = ReturnType<typeof useDiscoveryAppliedLineage>;

type AppliedLineageParams = {
  environmentId: number;
  columnNames?: string[] | null;
  tags?: string[] | null;
  types?: ResourceNodeType[];
  pause?: boolean;
} & OneOf<{ uniqueIds: string[] | null; select: string | null }>;

type Errors = {
  selectorInvalidResourceType?: ToastMessage | null;
  selectorInvalidFlag?: ToastMessage | null;
};

function shouldPauseLineage({
  uniqueIds,
  select,
  pause,
}: Pick<AppliedLineageParams, 'pause' | 'uniqueIds' | 'select'>) {
  return pause || (select == null && !uniqueIds?.length);
}

export const useAppliedLineage = ({
  environmentId,
  columnNames,
  tags,
  types,
  pause,
  uniqueIds,
  select,
}: AppliedLineageParams): AppliedLineageResult => {
  const [errors, setErrors] = useState<Errors>({});
  const { userId } = useEnvironmentScope();

  useEffect(() => {
    if (select == null) return;
    if (errors.selectorInvalidFlag) {
      toast.error(errors.selectorInvalidFlag, {
        id: 'selector-invalid-flag',
      });
    } else {
      toast.dismiss('selector-invalid-flag');
    }

    if (errors.selectorInvalidResourceType) {
      toast.error(errors.selectorInvalidResourceType, {
        id: 'selector-invalid-resource-type',
      });
    } else {
      toast.dismiss('selector-invalid-resource-type');
    }
  }, [errors, select]);

  const props = useMemo(() => {
    if (!select == null) return { select: null };

    const parsedFlags = zParseSelectFlags.safeParse(select);

    const inputs = parsedFlags.success ? parsedFlags.data : null;

    const types = toSearchResourceTypes(inputs?.resourceTypes ?? null);

    const errors = {
      selectorInvalidFlag: !parsedFlags.success
        ? // @ts-ignore -- this is a valid error message
          (
            <ReactMarkdown className="prose prose-sm">
              {parsedFlags.error.errors?.[0]?.message ?? ''}
            </ReactMarkdown>
          ) || 'Invalid selector'
        : null,
      selectorInvalidResourceType: types.error ? (
        <ReactMarkdown className="prose prose-sm">{types.error ?? ''}</ReactMarkdown>
      ) : null,
    };

    // eslint-disable-next-line react-compiler/react-compiler
    setErrors(errors);

    if (errors.selectorInvalidFlag || errors.selectorInvalidResourceType) {
      return { select: null };
    }

    return {
      select: inputs?.select ?? null,
      exclude: inputs?.exclude,
      types: types.success?.length ? types.success : undefined,
    };
  }, [select]);
  const lineageQuery = useDiscoveryAppliedLineage({
    environmentId,
    userId,
    columnNames,
    tags,
    types,
    uniqueIds,
    pause: shouldPauseLineage({ select: props.select, pause, uniqueIds }),
    ...props,
  });

  return lineageQuery;
};

type RawAppliedLineageNode = NonNullable<
  NonNullable<
    ReturnType<typeof useAppliedLineage>['data']
  >['environment']['applied']['lineage']
>[number] & {
  environmentId?: number;
  parents?: { uniqueId: string }[];
  environmentDeploymentType?: EnvironmentDeploymentType | null;
};

type RawProjectNode = {
  parentIds: string[];
  parents?: { uniqueId: string }[];
  resourceType: 'project';
  name: string;
  projectId: number;
  uniqueId: string;
  environmentId: number;
  hasReferences: boolean;
  projectExpandHandler?: () => void;
};

const useAppliedLineageWithExternalNodesRaw = (params: AppliedLineageParams) => {
  const lineageQuery = useAppliedLineage(params);
  const lineageData = lineageQuery.data;
  const upstreamPublicModels = useMemo(
    () =>
      lineageData?.environment.applied.lineage
        ?.flatMap((m) => ('publicParentIds' in m ? m.publicParentIds : []))
        .filter(unique),
    [lineageData?.environment.applied.lineage],
  );

  const { accountId, environmentId, projectId } = useDbtCloudProject();
  const producerProjectsQuery = useProducerProjects({
    environmentId,
    pause: !upstreamPublicModels?.length,
  });
  const producerEnvironmentIds = producerProjectsQuery.data?.producerProjects?.map(
    (project) => project.producerEnvironmentId,
  );
  const filteredProducerEnvironmentIds = producerEnvironmentIds?.filter(truthy);
  const missingProducerData =
    filteredProducerEnvironmentIds?.length !== producerEnvironmentIds?.length;
  useEffect(() => {
    if (!missingProducerData) return;
    toast.error('Lineage may be incomplete, some upstream project data is missing.', {
      id: 'producer-project-error',
    });
  }, [missingProducerData]);
  const shouldPause = shouldPauseLineage(params);
  const shouldFetchPublicModels = !shouldPause && !!upstreamPublicModels?.length;

  // use new envid filter in usePublicModels
  const publicModelsQuery = useAllPublicModels(
    useMemo(
      (): Parameters<typeof useAllPublicModels>[0] => ({
        loadingStrategy: 'waitForAllResults',
        accountId,
        environmentIds: filteredProducerEnvironmentIds,
        pause: !shouldFetchPublicModels,
      }),
      [accountId, shouldFetchPublicModels, filteredProducerEnvironmentIds],
    ),
  );
  const otherDependentProjectsQuery = useDependentProjects(environmentId, shouldPause);

  const publicModels = publicModelsQuery.data;
  const otherDependentProjects = otherDependentProjectsQuery.data;

  const fetching =
    lineageQuery.fetching ||
    publicModelsQuery.fetching ||
    otherDependentProjectsQuery.fetching;
  const error =
    lineageQuery.error || publicModelsQuery.error || otherDependentProjectsQuery.error;

  const isLoadingEnvironmentId = !!projectId && !environmentId;

  return useMemo(() => {
    if (shouldPause) {
      return {
        fetching: false,
        data: null,
        error: null,
        status: 'paused',
      } as const;
    }
    if (isLoadingEnvironmentId) {
      return {
        fetching: true,
        data: null,
        error: null,
        status: 'loading',
      } as const;
    }
    const upstreamExternalNodes = new Set<string>();
    const downstreamProjects: Record<string, RawProjectNode> = {};

    const lineage = lineageData?.environment.applied?.lineage;
    if (!environmentId) {
      // This should not be reachable under normal circumstances
      return {
        fetching: false,
        error: {
          message: `Invalid environment ID: '${environmentId}'`,
        },
        data: null,
        status: 'error',
      } as const;
    }
    if (error) {
      return { fetching, data: null, error, status: 'error' } as const;
    }
    if ((shouldFetchPublicModels && !publicModels) || fetching) {
      return { fetching, data: null, error: null, status: 'loading' } as const;
    }
    if (!lineage) {
      return { fetching: false, data: null, error: null, status: 'success' } as const;
    }
    const result: (RawAppliedLineageNode | RawProjectNode)[] = [];
    lineage.forEach((node) => {
      if (node && 'publicParentIds' in node) {
        node.publicParentIds.forEach((id) => {
          upstreamExternalNodes.add(id);
        });
      }

      result.push(node);

      const projectConnections = (otherDependentProjects ?? {})[node.uniqueId] as
        | ExternalConnections
        | undefined;
      projectConnections?.children.forEach((project) => {
        const numDependentModels = project.numDependentModels || 0;
        downstreamProjects[project.id] ??= {
          parentIds: [],
          name: project.label,
          projectId: project.projectId,
          environmentId: project.environmentId!,
          uniqueId: project.id,
          resourceType: 'project',
          hasReferences: false,
        };
        if (numDependentModels > 0) {
          downstreamProjects[project.id].hasReferences = true;
          // only show edge from public model to downstream project
          // if the project directly references the public model
          downstreamProjects[project.id].parentIds.push(node.uniqueId);
        }
      });
    });

    Object.values(downstreamProjects).forEach((node) => {
      // only make the project node appear if it has references,
      // i.e. edge from public model to downstream project
      if (node.hasReferences) {
        result.push(node);
      }
    });

    //  iterate over external nodes and hydrate with information from publicModels
    upstreamExternalNodes.forEach((id) => {
      const parsedId = parseUniqueId(id);
      const publicModel = publicModels?.find((m) => m.uniqueId === id);
      if (!publicModel) return;

      result.push({
        access: 'public',
        name: parsedId.remainder,
        uniqueId: parsedId.raw,
        projectId: publicModel.projectId,
        environmentId: publicModel.environmentId!,
        environmentDeploymentType:
          'environmentDeploymentType' in publicModel
            ? publicModel.environmentDeploymentType
            : null,
        alias: null,
        database: null,
        schema: null,
        filePath: '',
        parentIds: [],
        tags: [],
        group: null,
        publicParentIds: [],
        version: null,
        materializationType: publicModel.materializationType,
        lastRunStatus: null,
        fqn: publicModel.fqn,
      });
    });
    return { fetching: false, data: result, error: null, status: 'success' };
  }, [
    shouldPause,
    isLoadingEnvironmentId,
    lineageData?.environment.applied?.lineage,
    environmentId,
    error,
    shouldFetchPublicModels,
    publicModels,
    fetching,
    otherDependentProjects,
  ]);
};

export const toAppliedLineageNode = (
  node: RawAppliedLineageNode | RawProjectNode,
  currentProjectId: number,
  getProjectName: (projectId: number) => string | undefined,
  modelQueryHistoryState?: ModelQueryHistoryState,
): ExplorerDagNode => {
  const nodeProjectId = node.projectId || currentProjectId;
  const isProject = 'resourceType' in node && node.resourceType === 'project';
  const parsedUniqueId = isProject ? null : parseUniqueId(node.uniqueId);
  // the parentIds can be either parentIds or parents, we need to handle both cases
  const parentIds = (
    'parentIds' in node
      ? node.parentIds
      : 'parents' in node
        ? node?.parents?.map((p) => p.uniqueId)
        : []
  ) as string[];
  const publicParentIds = 'publicParentIds' in node ? node.publicParentIds : [];
  const usageLabel =
    modelQueryHistoryState?.state === ModelQueryHistoryStatus.Error
      ? 'Unknown'
      : modelQueryHistoryState?.state === ModelQueryHistoryStatus.NoData
        ? 'Pending'
        : undefined;

  const result: ExplorerDagNode = {
    id: node.uniqueId,
    environmentId: node.environmentId,
    environmentDeploymentType:
      'environmentDeploymentType' in node ? node.environmentDeploymentType : null,
    // Release version is unsupported in lineage endpoint
    releaseVersion: null,
    label:
      // auto exposures should not use remainder to show deduplication suffix
      // ex. 'customer360_test.MmE1YmUzYjUtNGZjOC00NDc0LWE2ZTktZWIyMDc4YjEyOGJl+3'
      parsedUniqueId?.resourceType === 'exposure'
        ? node.name!
        : (parsedUniqueId?.remainder ?? node.name ?? ''),
    resourceType: !parsedUniqueId ? ('project' as const) : parsedUniqueId.resourceType,
    parents: Array.from(new Set([...parentIds, ...publicParentIds])),
    data: {},
    group: 'group' in node ? (node.group ?? null) : null,
    access: 'access' in node ? (node.access as AccessType) : null,
    modelingLayer: 'modelingLayer' in node ? (node.modelingLayer as ModelLayer) : null,
    materializationType:
      'materializationType' in node
        ? (node.materializationType as MaterializationType)
        : null,
    dbtCloudProject: isProject ? node.name : (getProjectName(nodeProjectId) ?? ''),
    projectId: nodeProjectId,
    annotation: '',
    // tags: 'tags' in node ? node.tags ?? [] : [], // TODO: confirm we can remove this
    nodeStatus: 'lastRunStatus' in node ? (node.lastRunStatus as NodeStatus) : null,
    testStatus:
      'worstTestStatus' in node
        ? (node.worstTestStatus as TestStatus)
        : 'lastRunStatus' in node && node.lastRunStatus === 'reused'
          ? 'reused'
          : undefined,
    fqn: 'fqn' in node ? (node.fqn?.filter(truthy).join('.') ?? null) : null,
    sublabel: (() => {
      if (!parsedUniqueId) return null;
      switch (parsedUniqueId.resourceType) {
        case 'unit_test':
          return 'Unit Test';
        case 'test':
          return 'Data Test';
        default:
          return null;
      }
    })(),
    autoBiProvider:
      'autoBiProvider' in node ? (node.autoBiProvider as AutoExposureBiProvider) : null,
    usageQueryLabel: usageLabel,
    usageQueryCount:
      usageLabel === undefined
        ? 'usageQueryCount' in node
          ? node.usageQueryCount
            ? (node.usageQueryCount as number)
            : null
          : undefined
        : 0,
    isExternalNode: node.projectId !== currentProjectId,
  };

  if (isProject && node.hasReferences) {
    result.annotation = 'Referenced by';
  }

  return result;
};

export const useAppliedLineageWithExternalNodes = (params: AppliedLineageParams) => {
  const { projectId } = useDbtCloudProject();
  const { getProjectName } = useDbtCloudAccount();
  const { lensContext } = useDagContext();
  const [queryHistoryStateQuery] = useModelQueryHistoryState({
    environmentId: params.environmentId,
  });
  const queryHistoryStateHolder =
    queryHistoryStateQuery.data?.environment.modelQueryHistoryState;

  const lineageQuery = useAppliedLineageWithExternalNodesRaw(params);

  useEffect(() => {
    let receivedLensState: LensState;
    let stateMessage: string = '';
    let stateMessageDetails: string = '';

    switch (queryHistoryStateHolder?.state) {
      case ModelQueryHistoryStatus.Error:
        receivedLensState = 'error';
        stateMessage = 'Unknown error.';
        stateMessageDetails = `An error occurred loading usage data :${getHumanReadableQueryError(
          queryHistoryStateHolder.errorCategory,
          queryHistoryStateHolder.error,
        )}`;
        break;
      case ModelQueryHistoryStatus.NoData:
        receivedLensState = 'info';
        stateMessage = 'Pending Data';
        stateMessageDetails = QUERY_USAGE_TOOLTIP_NO_DATA;
        break;
      default:
        receivedLensState = 'success';
        break;
    }

    lensContext.lensState?.set('usageQueries', {
      state: receivedLensState,
      description: stateMessage,
      descriptionDetails: stateMessageDetails,
    });
  }, [queryHistoryStateHolder, lensContext.lensState]);

  const transformedData = useMemo(() => {
    return lineageQuery.data?.map((node) =>
      toAppliedLineageNode(node, projectId, getProjectName, queryHistoryStateHolder),
    );
  }, [lineageQuery.data, projectId, getProjectName, queryHistoryStateHolder]);

  return useMemo(() => {
    return {
      fetching: lineageQuery.fetching,
      error: lineageQuery.error,
      status: lineageQuery.status,
      data: transformedData,
      adapterType: queryHistoryStateQuery.data?.environment.adapterType,
    };
  }, [
    lineageQuery.fetching,
    lineageQuery.error,
    lineageQuery.status,
    transformedData,
    queryHistoryStateQuery.data?.environment.adapterType,
  ]);
};
